import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import debounce from 'lodash/debounce'
import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
import React from 'react'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { formatMinMaxDisplayValues } from '@unreserved-frontend-v2/utils/format-min-max-display-values'
import { NoOp } from '@unreserved-frontend-v2/utils/noop'
import { ReactFunctionalComponent } from '@unreserved-frontend-v2/utils/types'
import { KeyValuePair } from '@unreserved-frontend-v2/utils/types'

import {
  FILTER_GROUPS_DESKTOP_HEIGHT,
  FilterContainerWrapperPlaceholder,
  FilterContainerWrapperProps,
  HORIZONTAL_PICKER_LITE_DESKTOP_HEIGHT,
  POPOVER_BODY_STYLES,
  POPOVER_CTA_STYLES,
  POPOVER_FILTER_CONTAINER_STYLE,
  POPOVER_HEADER_STYLES,
} from './layout'
import { FiltersBarProps } from './props'
import { HorizontalPickerLiteProps } from '../horizontal-picker-lite/horizontal-picker-lite'
import InputField from '../input-field/input-field'
import { Filter, FilterChangeEvent, FilterType, FilterValue, FilterValueSelect } from '../schema-table/models'
import { SortFilterProps } from '../sort-filter/sort-filter'
import { useTailwindConfig } from '../tailwind/use-tailwind-config'
import { UiToggleButtonGroupProps } from '../ui-toggle-button-group/ui-toggle-button-group'

const UiToggleButtonGroup: React.ComponentType<UiToggleButtonGroupProps> = dynamic(
  () => import('../ui-toggle-button-group/ui-toggle-button-group'),
  { ssr: false }
)
const UiToggleButtonGroupTiled: React.ComponentType<UiToggleButtonGroupProps> = dynamic(
  () => import('../ui-toggle-button-group/ui-toggle-button-group'),
  { ssr: false }
)
const HorizontalPickerLite: React.ComponentType<HorizontalPickerLiteProps> = dynamic(
  () => import('../horizontal-picker-lite/horizontal-picker-lite')
)
const Popover = dynamic(() => import('@mui/material/Popover'), { ssr: false })
const SortFilter: React.ComponentType<SortFilterProps> = dynamic(
  () => import('../sort-filter/sort-filter').then((mod) => mod.SortFilter),
  { ssr: false, loading: () => <FilterContainerWrapperPlaceholder className="relative h-8 w-[35px] rounded" /> }
)
const SelectRanged = dynamic(() => import('../select-ranged/select-ranged'), {
  ssr: false,
})
const Select = dynamic(() => import('../select/select'), { ssr: false })
const FontAwesomeIcon: React.ComponentType<FontAwesomeIconProps> = dynamic(
  () => import('@fortawesome/react-fontawesome').then((mod) => mod.FontAwesomeIcon),
  { ssr: false }
)
const PrimaryFilterContainerWrapper: React.ComponentType<FilterContainerWrapperProps> = dynamic(
  () => import('./layout').then((mod) => mod.FilterContainerWrapper),
  {
    ssr: false,
    loading: () => <FilterContainerWrapperPlaceholder className="w-[250px] rounded-tl rounded-bl" />,
  }
)
const FilterContainerCapWrapper: React.ComponentType<FilterContainerWrapperProps> = dynamic(
  () => import('./layout').then((mod) => mod.FilterContainerWrapper),
  {
    ssr: false,
    loading: () => <FilterContainerWrapperPlaceholder className="rounded-tr rounded-br" />,
  }
)
const FilterContainerWrapper: React.ComponentType<FilterContainerWrapperProps> = dynamic(
  () => import('./layout').then((mod) => mod.FilterContainerWrapper),
  {
    ssr: false,
    loading: () => <FilterContainerWrapperPlaceholder />,
  }
)
const FilterContainerWrapperToggles: React.ComponentType<FilterContainerWrapperProps> = dynamic(
  () => import('./layout').then((mod) => mod.FilterContainerWrapper),
  {
    ssr: false,
    loading: () => <FilterContainerWrapperPlaceholder className="max-w-[222px] rounded" />,
  }
)

const SEARCH_QUERY_DEBOUNCE = 150

export const PopoverFilterCTA = ({ label }: { label: string | undefined }) => {
  return (
    <>
      {label}
      <FontAwesomeIcon className="absolute right-3" transform={{ size: 11, y: 4 }} icon={faChevronDown} />
    </>
  )
}

const delayedSearchQuery = debounce(({ key, value }: FilterChangeEvent, onChange: (e: FilterChangeEvent) => void) => {
  onChange({ key, value })
}, SEARCH_QUERY_DEBOUNCE)

export const Filters = ({
  filters,
  onChange,
  onLinkClick = NoOp,
  values,
  selections,
  searchKeywordPlaceholder,
  className,
  listDisplayToggler,
}: FiltersBarProps) => {
  const { t } = useTranslation()
  const { primary, shades } = useTailwindConfig()

  const hasPrimaryFilter = filters[0].type === FilterType.External

  // The primary filter is first.
  const primaryFilter = hasPrimaryFilter ? filters[0] : null

  // Then the secondary filters.
  const secondaryFilters = hasPrimaryFilter ? filters.slice(1, filters.length) : filters

  // Then the horizontal picker.
  const horizontalFilters = filters.filter(({ type }) => type === FilterType.HorizontalPicker)

  // Then the sort filter.
  const sortFilter = filters[filters.length - 1]

  const filtersVisibility = filters.map((_) => false)
  const [visibleStates, setVisibileStates] = useState(filtersVisibility)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleFilterToggle = (event: React.MouseEvent<HTMLElement>, i: number) => {
    setAnchorEl(event.currentTarget)
    setVisibileStates((current) => {
      current[i] = !current[i]
      return [...current]
    })
  }

  const handleClose = () => {
    setVisibileStates(filtersVisibility)
  }

  const popoverFilterContainerClasses = (f: Filter, i: number) =>
    twMerge(
      `relative z-[101] w-full items-center bg-white ${i !== 0 ? 'border-l' : ''} ${
        visibleStates[i] ? 'border-primary relative z-[102] -mr-[1px] border-r' : 'border-shades-200'
      } ${f.classes}`
    )

  const getToggleButtonLabel = (key: string, label?: string) => {
    if (selections[key] === 'ALL') {
      return label
    }

    const valueKey = (values[key] as FilterValue[]).find((v) => v.value === selections[key])?.key

    return valueKey ? t(valueKey) : label
  }

  return (
    <>
      <div
        className={twMerge('flex justify-between border-shades-200', className)}
        style={{ height: FILTER_GROUPS_DESKTOP_HEIGHT }}
      >
        {primaryFilter?.type === FilterType.External ? (
          <PrimaryFilterContainerWrapper
            className={twMerge(`w-full flex-grow border-shades-200`, primaryFilter.classes)}
            noDropShadowFix={true}
          >
            {React.createElement(primaryFilter.externalComponentType as ReactFunctionalComponent, {
              location: selections['geo'],
              onChange: onChange,
              searchKeywordPlaceholder: searchKeywordPlaceholder,
            })}
          </PrimaryFilterContainerWrapper>
        ) : null}

        {secondaryFilters.map((f, i) => {
          const WrapperContainer = f.isCappedWrapper ? FilterContainerCapWrapper : FilterContainerWrapper

          switch (f.type) {
            case FilterType.Spacer: {
              return <div key={`spacer-${i}`} className={twMerge('mr-3', f.classes)} />
            }
            case FilterType.External: {
              return React.createElement(f.externalComponentType as ReactFunctionalComponent, {
                ...f.externalComponentProps,
                // In the case of `External`, values are considered runtime `externalComponentProps`
                ...values[f.key],
                // TODO is there a generic way to handle this?
                ...(selections['geo'] ? { location: selections['geo'] } : {}),
                ...(searchKeywordPlaceholder ? { searchKeywordPlaceholder } : {}),
                key: f.key,
                containerClasses: popoverFilterContainerClasses(f, i),
                handleFilterToggle,
                filterIndex: i,
                anchorEl,
                isOpen: visibleStates[i],
                onClose: handleClose,
                onChange: onChange,
              })
            }
            case FilterType.Search: {
              const value = selections[f.key] === 'ALL' ? '' : selections[f.key]

              // If the placeholder key doesn't contain ':' then we don't have to translate it so use the value
              // directly (e.g. - when the value comes straight from the back end).
              const placeholder = f.placeholder?.indexOf(':') === -1 ? f.placeholder : t(f.placeholder || '')

              return (
                <WrapperContainer key={f.key} className={twMerge(`flex-grow ${f.classes}}`)}>
                  <InputField
                    id={f.key}
                    placeholder={placeholder}
                    defaultValue={value}
                    startIcon={f.icon}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      delayedSearchQuery({ key: f.key, value: e.target.value }, onChange)
                    }}
                    sxValues={{
                      '& .MuiOutlinedInput-input': {
                        paddingTop: i === 0 ? '11px' : '9px',
                        paddingBottom: i === 0 ? '11px' : '10px',
                      },
                      '& .MuiInputBase-root fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: shades['200'],
                        borderWidth: '1px',
                        borderRightWidth: '0px',
                        borderBottomLeftRadius: i === 0 ? '4px' : '0px',
                        borderTopLeftRadius: i === 0 ? '4px' : '0px',
                      },
                      '& .MuiInputBase-root.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: primary['DEFAULT'],
                        borderRightWidth: '1px',
                      },
                      ...(f.sxValues ? f.sxValues : {}),
                    }}
                    removeBorderStyles={true}
                    className="bg-white"
                  />
                </WrapperContainer>
              )
            }
            case FilterType.Select: {
              return (
                <WrapperContainer
                  key={f.key}
                  className={twMerge(popoverFilterContainerClasses(f, i), 'relative border-0')}
                >
                  <Select
                    id={f.key}
                    endIcon={faChevronDown}
                    values={values[f.key] as FilterValue[]}
                    selectedValue={selections[f.key] as string}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange({ key: f.key, value: e.target.value })
                    }
                    fullWidth={true}
                    removeBorderStyles={true}
                    endIconTransform={{ size: 11, y: 1 }}
                    sxValues={{
                      '& .MuiOutlinedInput-input.MuiSelect-select': {
                        paddingTop: '9px',
                        paddingBottom: '10px',
                      },
                      '& .MuiInputBase-root fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: shades['200'],
                        borderWidth: '1px',
                        borderRightWidth: '0px',
                        borderBottomLeftRadius: '0px',
                        borderTopLeftRadius: '0px',
                      },
                      '& .MuiInputBase-root.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: primary['DEFAULT'],
                        borderRightWidth: '1px',
                      },
                      '.MuiOutlinedInput-root': {
                        paddingTop: '0px',
                        paddingBottom: '0px',
                      },
                      ...(f.sxValues ? f.sxValues : {}),
                    }}
                  />
                </WrapperContainer>
              )
            }
            case FilterType.SelectRanged: {
              const { key: minDataKey, values: minValues, suffixRemovalStr } = values[f.key][0] as FilterValueSelect
              const { key: maxDataKey, values: maxValues } = values[f.key][1] as FilterValueSelect

              const minLabel = minValues.find((item) => item.value.toString() === selections[minDataKey])?.key
              const maxLabel = maxValues.find((item) => item.value.toString() === selections[maxDataKey])?.key
              const label = formatMinMaxDisplayValues(minLabel, maxLabel, f.label, suffixRemovalStr)

              return (
                <WrapperContainer
                  key={f.key}
                  className={popoverFilterContainerClasses(f, i)}
                  style={POPOVER_FILTER_CONTAINER_STYLE}
                >
                  <div className={POPOVER_CTA_STYLES} onClick={(e) => handleFilterToggle(e, i)}>
                    <PopoverFilterCTA label={label} />
                  </div>
                  <Popover
                    id={`filter${i}`}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: 47, horizontal: -1 }}
                    open={visibleStates[i]}
                    onClose={handleClose}
                    sx={{ '.MuiPaper-root': { padding: 0 } }}
                  >
                    <div className="h-[234]px w-[358px]">
                      <div className={POPOVER_HEADER_STYLES}>{f.headerLabel}</div>
                      <div className={POPOVER_BODY_STYLES}>
                        <SelectRanged
                          minValues={minValues}
                          maxValues={maxValues}
                          minValue={selections[minDataKey] as string | number}
                          maxValue={selections[maxDataKey] as string | number}
                          onChange={(e: { key: string; value: [number | string, number | string] }) => {
                            const min = e.value[0]
                            const max = e.value[1]
                            onChange([
                              // TODO: if we re-enable autocomplete, we'll need this
                              // { key: minDataKey, value: min?.toString() },
                              // { key: maxDataKey, value: max?.toString() },
                              { key: minDataKey, value: min === 'ALL' ? undefined : min.toString() },
                              { key: maxDataKey, value: max === 'ALL' ? undefined : max.toString() },
                            ])
                          }}
                        />
                      </div>
                    </div>
                  </Popover>
                </WrapperContainer>
              )
            }

            case FilterType.ToggleButtonGroup: {
              if (!values[f.key].length) {
                return null
              }

              if (!f.tileGrid) {
                return (
                  <FilterContainerWrapperToggles key={f.key} className={twMerge(`${f.classes ?? ''} [&>*]:h-full`)}>
                    {f.label ? <h5>{t(f.label)}</h5> : null}
                    <UiToggleButtonGroup
                      fullWidth={true}
                      onChange={(_: React.MouseEvent<HTMLElement>, value) => {
                        onChange({ key: f.key, value })
                      }}
                      value={selections[f.key]}
                      items={values[f.key] as FilterValue[]}
                      className="bg-white"
                      itemClass="py-3 h-full indent-1 leading-[18px] w-[45px] min-w-[45px]"
                      iconStart={f.icon}
                    />
                  </FilterContainerWrapperToggles>
                )
              }

              return (
                <div key={f.key} className={popoverFilterContainerClasses(f, i)} style={POPOVER_FILTER_CONTAINER_STYLE}>
                  <div className={POPOVER_CTA_STYLES} onClick={(e) => handleFilterToggle(e, i)}>
                    <PopoverFilterCTA label={getToggleButtonLabel(f.key, f.label)} />
                  </div>
                  <Popover
                    id={`filter${i}`}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: 47, horizontal: -1 }}
                    open={visibleStates[i]}
                    onClose={handleClose}
                    sx={{ '.MuiPaper-root': { padding: 0 } }}
                  >
                    <div className="h-[234]px w-[358px]">
                      <div className={POPOVER_HEADER_STYLES}>{f.headerLabel}</div>
                      <div className={POPOVER_BODY_STYLES}>
                        <UiToggleButtonGroupTiled
                          fullWidth={true}
                          onChange={(_: React.MouseEvent<HTMLElement>, value) => {
                            onChange({ key: f.key, value })
                          }}
                          value={selections[f.key]}
                          items={values[f.key] as FilterValue[]}
                          className="bg-white "
                          iconStart={f.icon}
                          tileGrid={true}
                          dynamicHeight={true}
                        />
                      </div>
                    </div>
                  </Popover>
                </div>
              )
            }
            default:
              return <div key={i} />
          }
        })}
      </div>

      <div className="flex w-full items-center pt-[5px] pb-[4px]">
        <div className="-ml-6 flex flex-grow">
          {horizontalFilters.map((horizontalFilter, indx) => {
            return (
              <HorizontalPickerLite
                key={horizontalFilter.key}
                filterKey={horizontalFilter.key}
                onChange={onChange}
                onLinkClick={onLinkClick}
                items={values[horizontalFilter.key] as FilterValue[]}
                selectedItem={selections[horizontalFilter.key] as string}
                innerClassName={indx < horizontalFilters.length ? 'mr-0' : ''}
                className={twMerge(`h-[${HORIZONTAL_PICKER_LITE_DESKTOP_HEIGHT}px]`, horizontalFilter.classes)}
              />
            )
          })}
        </div>

        {listDisplayToggler ? listDisplayToggler : null}

        {sortFilter.type === FilterType.Sort ? (
          <SortFilter
            currentSort={selections['sort'] as string}
            values={values[sortFilter.key] as KeyValuePair<string>[]}
            onChange={onChange}
          />
        ) : null}
      </div>
    </>
  )
}
